import React, {
    createContext,
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { BC_MIN_AGE } from "@/constants/bodyScan";
import { COMPONENTS, components, getInitComponent } from "@/constants/components";
import { ExperimentIds } from "@/constants/experiments";
import { imagesToPreload } from "@/constants/imagesToPreload";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import { useEnableExps } from "@/hooks/useEnableExps";
import { useLocalization } from "@/hooks/useLocalization";
import { useExperiment } from "@/utils/ExperimentsProvider";
import { useGender } from "@/utils/GenderProvider";
import { useCurrency } from "@/utils/GeolocationProvider";
import { lazyWithPreload } from "@/utils/lazyPreload";
import { localStorageManager, LocalStorageManagerKeys } from "@/utils/localStorageManager";
import PaymentProvider from "@/utils/payment/PaymentProvider";
import useAnswers from "@/utils/useAnswers";

import LoaderScreenVariantsProvider from "./LoaderScreenVariantsProvider";
import { FlowTypes } from "./types";
import { getFlowTypesToBooleanObj } from "./utils";

const TestNewPaymentPaywall = lazyWithPreload(
    () => import("@/components/Paywall/TestNewPaymentPaywall")
);

export interface FlowProviderType {
    initComponent: COMPONENTS | null;
    component: COMPONENTS | null;
    setComponent: React.Dispatch<React.SetStateAction<COMPONENTS | null>>;
    flowType: FlowTypes | null;
}

const defaultState: FlowProviderType = {
    component: COMPONENTS.LOADER,
    initComponent: null,
    setComponent: () => null,
    flowType: null,
};

export const FlowProviderContext = createContext<FlowProviderType>(defaultState);

interface FlowProviderProps {
    flowType?: FlowTypes | null;
}

export const FlowProvider = ({ flowType = null }: FlowProviderProps) => {
    const { loadCurrencies, isLoaded: isCurrencyLoaded } = useCurrency();

    const initComponent = useMemo(() => getInitComponent({ flowType }), [flowType]);

    const { isTest: isNewPaymentTest } = useExperiment({
        experimentId: ExperimentIds.NEW_PAYMENT_VERSION,
    });

    const [component, setComponent] = useState<COMPONENTS | null>(
        initComponent === COMPONENTS.AI_PAYWALL ? null : initComponent
    );

    const Component = useMemo(() => {
        if (component && component === COMPONENTS.AI_PAYWALL && isNewPaymentTest) {
            return TestNewPaymentPaywall;
        }
        return component && components[component];
    }, [component, isNewPaymentTest]);

    useEffect(() => {
        loadCurrencies();
    }, [loadCurrencies]);

    useEffect(() => {
        if (initComponent === COMPONENTS.AI_PAYWALL && isCurrencyLoaded) {
            setComponent(initComponent);
        }
    }, [initComponent, isCurrencyLoaded]);

    return (
        <FlowProviderContext.Provider
            value={{
                component,
                initComponent,
                setComponent,
                flowType,
            }}
        >
            <PaymentProvider>
                <LoaderScreenVariantsProvider>
                    {Component && <Component />}
                </LoaderScreenVariantsProvider>
            </PaymentProvider>
        </FlowProviderContext.Provider>
    );
};

interface FlowManagerProps {
    nextComponent?: COMPONENTS;
    componentsToPreload?: COMPONENTS[];
}
export const useFlowManager = ({ nextComponent, componentsToPreload }: FlowManagerProps) => {
    const { component, initComponent, setComponent, flowType } = useContext(FlowProviderContext);
    const isFlowType = useMemo(() => getFlowTypesToBooleanObj(flowType), [flowType]);
    const { answers } = useAnswers();
    const { isFemale } = useGender();
    const { isEnglish } = useLocalization();

    const isSkipNutritionBook = useMemo(() => {
        const isSkip = localStorageManager.getStoredItem(
            LocalStorageManagerKeys.IS_SKIP_NUTRITION_BOOK
        );
        const result = isSkip || Number(answers[QUESTIONS_IDS.AGE]) < BC_MIN_AGE;
        localStorageManager.storeItem(LocalStorageManagerKeys.IS_SKIP_NUTRITION_BOOK, result);
        return result;
    }, [answers]);

    useEnableExps({
        isFlowType: isFlowType,
        initComponent,
        nextComponent,
        currentComponent: component,
    });

    useEffect(() => {
        if (nextComponent) {
            const Component = components[nextComponent];
            const images = imagesToPreload(isFemale)[nextComponent];
            Component?.preload(images);
        }
    }, [isFemale, nextComponent]);

    useEffect(() => {
        if (componentsToPreload) {
            for (const component of componentsToPreload) {
                const Component = components[component];
                const images = imagesToPreload(isFemale)[component];
                Component?.preload(images);
            }
        }
    }, [componentsToPreload, isFemale]);

    const handleContinue = useCallback(
        (component?: COMPONENTS) => {
            if (nextComponent) {
                if (nextComponent === COMPONENTS.BODY_SCAN) {
                    if (isEnglish) {
                        setComponent(COMPONENTS.WORKBOOK);
                    } else {
                        setComponent(COMPONENTS.DEEPLINK);
                    }
                } else {
                    setComponent(nextComponent);
                }
            } else if (component) {
                setComponent(component);
            }
        },
        [isEnglish, nextComponent, setComponent]
    );

    return {
        component,
        onContinue: handleContinue,
        flowType,
        isFlowType,
        isSkipNutritionBook,
    };
};

export default memo(FlowProvider);
